import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixDataV1DataPermissions from './data-v1-data-permissions-permissions.http.js';

/**
 * Permissions that determine who can access or perform actions on items in a data collection.
 *
 * Learn more about [collection permissions](https://support.wix.com/en/article/cms-collection-permissions-overview).
 */
export interface DataPermissions {
  /**
   * ID of the collection for these permissions.
   * @immutable
   * @maxLength 255
   */
  _id?: string;
  /**
   * Which users can read items from the collection.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemRead?: AccessLevelWithLiterals;
  /**
   * Which users can create new items in the collection.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemInsert?: AccessLevelWithLiterals;
  /**
   * Which users can update existing items in the collection.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemUpdate?: AccessLevelWithLiterals;
  /**
   * Which users can remove items from the collection.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemRemove?: AccessLevelWithLiterals;
  /**
   * Special permissions granted to specified users or roles.
   *
   * > **Note:** Special permissions override [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions).
   * @readonly
   * @maxSize 1000
   */
  specialPermissions?: SpecialPermissions[];
  /**
   * Permissions for managing drafts and publish status in the collection.
   *
   * Present only when the collection has the draft items plugin.
   * @internal
   */
  draftItemsPluginPermissions?: DraftItemsPluginPermissions;
}

/** Minimal access level required to perform an action on collection items. Each access level includes the permissions of the access levels below it. */
export enum AccessLevel {
  /** Anyone, including [site visitors](https://dev.wix.com/docs/rest/articles/get-started/about-identities#site-visitor). */
  ANYONE = 'ANYONE',
  /** Logged-in [site members](https://dev.wix.com/docs/rest/articles/get-started/about-identities#site-member) and [collaborators](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site). */
  SITE_MEMBER = 'SITE_MEMBER',
  /** Logged-in [site members](https://dev.wix.com/docs/rest/articles/get-started/about-identities#site-member) and [collaborators](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site) who can only access items they created. */
  SITE_MEMBER_AUTHOR = 'SITE_MEMBER_AUTHOR',
  /** Site [collaborators with a CMS editor role](https://support.wix.com/en/article/roles-permissions-overview#roles-for-integrated-wix-apps). */
  CMS_EDITOR = 'CMS_EDITOR',
  /**
   * Site administrators, [collaborators](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site), or roles with special permissions.
   *
   * Learn more about [roles for site management](https://support.wix.com/en/article/roles-permissions-overview#general-roles-for-site-management).
   */
  PRIVILEGED = 'PRIVILEGED',
}

/** @enumType */
export type AccessLevelWithLiterals =
  | AccessLevel
  | 'ANYONE'
  | 'SITE_MEMBER'
  | 'SITE_MEMBER_AUTHOR'
  | 'CMS_EDITOR'
  | 'PRIVILEGED';

export interface SpecialPermissions extends SpecialPermissionsSubjectOneOf {
  /**
   * ID of the user for these special permissions.
   * @immutable
   * @maxLength 64
   */
  userId?: string;
  /**
   * ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.
   *
   * To find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.
   *
   * Learn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).
   * @immutable
   * @maxLength 64
   */
  policyId?: string;
  /**
   * ID of the special permissions set.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Whether the specified users or roles can read items from the collection.
   *
   * When set to `ALLOWED`, users can read items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemRead?: AccessWithLiterals;
  /**
   * Whether the specified users or roles can create new items in the collection.
   *
   * When set to `ALLOWED`, users can create new items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemInsert?: AccessWithLiterals;
  /**
   * Whether the specified users or roles can update existing items in the collection.
   *
   * When set to `ALLOWED`, users can update existing items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemUpdate?: AccessWithLiterals;
  /**
   * Whether the specified users or roles can remove items from the collection.
   *
   * When set to `ALLOWED`, users can remove items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemRemove?: AccessWithLiterals;
  /**
   * Whether the specified users or roles can manage drafts and publish/unpublish data items in the collection.
   * When set to `ALLOWED`, users can manage drafts and publish/unpublish data items.
   * @internal
   */
  draftItemsPluginPermissions?: SpecialPermissionsDraftItemsPluginPermissions;
}

/** @oneof */
export interface SpecialPermissionsSubjectOneOf {
  /**
   * ID of the user for these special permissions.
   * @immutable
   * @maxLength 64
   */
  userId?: string;
  /**
   * ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.
   *
   * To find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.
   *
   * Learn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).
   * @immutable
   * @maxLength 64
   */
  policyId?: string;
}

export enum Access {
  /** No special permissions. [Collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply to this action. */
  UNSPECIFIED = 'UNSPECIFIED',
  /** User or role has special permissions to perform this action, overriding [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions). */
  ALLOWED = 'ALLOWED',
}

/** @enumType */
export type AccessWithLiterals = Access | 'UNSPECIFIED' | 'ALLOWED';

export interface SpecialPermissionsDraftItemsPluginPermissions {
  /** Whether the specified users or roles can manage drafts in the collection. When set to `ALLOWED`, users can manage drafts. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply. */
  manageDrafts?: AccessWithLiterals;
  /** Whether the specified users or roles can manage publish status in the collection. When set to `ALLOWED`, users can manage publish status. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply. */
  managePublishStatus?: AccessWithLiterals;
}

/** Collection-level permissions contributed by the draft items plugin. */
export interface DraftItemsPluginPermissions {
  /** Access level required to manage drafts (view, edit, and remove draft items) in the collection. */
  manageDrafts?: AccessLevelWithLiterals;
  /** Access level required to publish draft items and unpublish published items in the collection. */
  managePublishStatus?: AccessLevelWithLiterals;
}

export interface GetPermissionsRequest {
  /**
   * ID of the collection to retrieve permissions of.
   * @maxLength 256
   */
  dataCollectionId: string;
}

export interface GetPermissionsResponse {
  /** Permissions of the specified collection. */
  dataPermissions?: DataPermissions;
}

export interface UpdatePermissionsRequest {
  /** Permissions to update. */
  dataPermissions: DataPermissions;
}

export interface UpdatePermissionsResponse {
  /** Updated permissions of the specified collection. */
  dataPermissions?: DataPermissions;
}

export interface AddSpecialPermissionsRequest {
  /**
   * ID of the collection for which to grant special permissions.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Special permissions to grant to the user or role for the specified collection. */
  specialPermissions: SpecialPermissions;
}

export interface AddSpecialPermissionsResponse {
  /** Special permissions granted to the user or role for the specified collection. */
  specialPermissions?: SpecialPermissions;
}

export interface UpdateSpecialPermissionsRequest {
  /** Special permissions to update for the specified user or role. */
  specialPermissions: SpecialPermissions;
}

export interface UpdateSpecialPermissionsResponse {
  /** Special permissions updated for the specified user or role. */
  specialPermissions?: SpecialPermissions;
}

export interface RemoveSpecialPermissionsRequest {
  /**
   * ID of the special permissions to remove.
   * @format GUID
   */
  specialPermissionsId: string;
}

export interface RemoveSpecialPermissionsResponse {}

export interface GetMyPermissionsRequest {
  /**
   * ID of the collection for which to check the current user's permissions.
   * @maxLength 256
   */
  dataCollectionId: string;
}

export interface GetMyPermissionsResponse {
  /** Whether the current user can read items from the collection. */
  itemRead?: boolean;
  /** Whether the current user can create new items in the collection. */
  itemInsert?: boolean;
  /** Whether the current user can update existing items in the collection. */
  itemUpdate?: boolean;
  /** Whether the current user can remove items from the collection. */
  itemRemove?: boolean;
  /**
   * Draft management operations the current user can perform in the collection.
   *
   * Present only when the collection has the draft items plugin.
   * @internal
   */
  draftItemsPluginPermissions?: GetMyPermissionsResponseDraftItemsPluginPermissions;
}

/** Draft management operations the current user is allowed to perform, contributed by the draft items plugin. */
export interface GetMyPermissionsResponseDraftItemsPluginPermissions {
  /** Whether the current user can manage drafts (view, edit, and remove draft items) in the collection. */
  manageDrafts?: boolean;
  /** Whether the current user can manage publish status (publish and unpublish items) in the collection. */
  managePublishStatus?: boolean;
}

export interface MigrateCustomRolesRequest {
  /**
   * ID of the role to update. If empty, all roles in the site are updated.
   * @format GUID
   */
  policyId?: string | null;
}

export interface MigrateCustomRolesResponse {}

export interface PolicyAssignmentsUpdated
  extends PolicyAssignmentsUpdatedEventOneOf {
  policyAssigned?: PolicyAssigned;
  policyUnassigned?: PolicyUnassigned;
  policyAssignmentUpdated?: PolicyAssignmentUpdated;
}

/** @oneof */
export interface PolicyAssignmentsUpdatedEventOneOf {
  policyAssigned?: PolicyAssigned;
  policyUnassigned?: PolicyUnassigned;
  policyAssignmentUpdated?: PolicyAssignmentUpdated;
}

export interface PolicyAssigned {
  subject?: Subject;
  assignment?: PolicyAssignment[];
}

export interface Subject {
  /** ID of identity assigned to the asset. */
  _id?: string;
  /** Type of identity assigned to the asset. Supported subject types include user IDs, account IDs, and app IDs. */
  subjectType?: SubjectTypeWithLiterals;
  /** Context of identity assigned to the asset. For example, a `subjectType` = `USER` will have `context` = `ACCOUNT`. */
  context?: SubjectContext;
}

export enum SubjectType {
  UNKNOWN = 'UNKNOWN',
  ACCOUNT = 'ACCOUNT',
  USER = 'USER',
  USER_GROUP = 'USER_GROUP',
  MEMBER_GROUP = 'MEMBER_GROUP',
  VISITOR_GROUP = 'VISITOR_GROUP',
  EXTERNAL_APP = 'EXTERNAL_APP',
  ACCOUNT_GROUP = 'ACCOUNT_GROUP',
  WIX_APP = 'WIX_APP',
}

/** @enumType */
export type SubjectTypeWithLiterals =
  | SubjectType
  | 'UNKNOWN'
  | 'ACCOUNT'
  | 'USER'
  | 'USER_GROUP'
  | 'MEMBER_GROUP'
  | 'VISITOR_GROUP'
  | 'EXTERNAL_APP'
  | 'ACCOUNT_GROUP'
  | 'WIX_APP';

export interface SubjectContext {
  _id?: string;
  contextType?: SubjectContextTypeWithLiterals;
}

export enum SubjectContextType {
  UNKNOWN_CTX = 'UNKNOWN_CTX',
  ORG_CTX = 'ORG_CTX',
  ACCOUNT_CTX = 'ACCOUNT_CTX',
}

/** @enumType */
export type SubjectContextTypeWithLiterals =
  | SubjectContextType
  | 'UNKNOWN_CTX'
  | 'ORG_CTX'
  | 'ACCOUNT_CTX';

export interface PolicyAssignment {
  policyId?: string;
  resource?: ResourcePath;
  condition?: PolicyCondition;
  assignmentId?: string | null;
}

export interface ResourcePath {
  organization?: string | null;
  account?: string | null;
  site?: string | null;
  resource?: Resource;
}

/**
 * A custom resource. Is used to represent some asset that is not a direct resource context (site or account), but something custom.
 * For example: payment method, blog post, domain, logo.
 */
export interface Resource {
  /** The resource id. */
  _id?: string | null;
  /** The resource type */
  type?: string | null;
}

export interface PolicyCondition {
  /** The type of the condition */
  condition?: ConditionType;
}

export interface ConditionType extends ConditionTypeOfOneOf {
  /** @deprecated */
  simpleCondition?: SimpleCondition;
  /** A logic combination between several conditions, with an operator between them */
  joinedConditions?: JoinedCondition;
  /** @deprecated */
  environmentCondition?: EnvironmentCondition;
  /** A single condition */
  condition?: Condition;
}

/** @oneof */
export interface ConditionTypeOfOneOf {
  /** @deprecated */
  simpleCondition?: SimpleCondition;
  /** A logic combination between several conditions, with an operator between them */
  joinedConditions?: JoinedCondition;
  /** @deprecated */
  environmentCondition?: EnvironmentCondition;
  /** A single condition */
  condition?: Condition;
}

export interface SimpleCondition {
  attrName?: string;
  value?: SimpleConditionValue;
  op?: SimpleConditionOperatorWithLiterals;
  conditionModelId?: string;
}

export interface SimpleConditionValue extends SimpleConditionValueValueOneOf {
  attrName?: string;
  stringValue?: string;
  boolValue?: boolean;
}

/** @oneof */
export interface SimpleConditionValueValueOneOf {
  attrName?: string;
  stringValue?: string;
  boolValue?: boolean;
}

export enum SimpleConditionOperator {
  UNKNOWN_SIMPLE_OP = 'UNKNOWN_SIMPLE_OP',
  EQUAL = 'EQUAL',
}

/** @enumType */
export type SimpleConditionOperatorWithLiterals =
  | SimpleConditionOperator
  | 'UNKNOWN_SIMPLE_OP'
  | 'EQUAL';

export interface JoinedCondition {
  /** The operator that should be used when evaluating the condition */
  op?: JoinedConditionOperatorWithLiterals;
  /** The conditions that should be evaluated, and then joined using the operator provided */
  conditions?: ConditionType[];
}

export enum JoinedConditionOperator {
  UNKNOWN_JOIN_OP = 'UNKNOWN_JOIN_OP',
  OR = 'OR',
  AND = 'AND',
}

/** @enumType */
export type JoinedConditionOperatorWithLiterals =
  | JoinedConditionOperator
  | 'UNKNOWN_JOIN_OP'
  | 'OR'
  | 'AND';

export interface EnvironmentCondition
  extends EnvironmentConditionConditionOneOf {
  experimentCondition?: ExperimentCondition;
}

/** @oneof */
export interface EnvironmentConditionConditionOneOf {
  experimentCondition?: ExperimentCondition;
}

export interface ExperimentCondition {
  spec?: string;
  fallbackValue?: string;
  expectedValue?: string;
}

export interface Condition {
  /** The unique identifier of the condition model. Indicates which actions the condition is working on */
  conditionModelId?: string;
  /** The operator that should be evaluated */
  operator?: ConditionOperator;
}

export interface ConditionOperator extends ConditionOperatorOperatorsOneOf {
  /** Comparison of equality - will be evaluated to true if the given parties are equal */
  equals?: EqualOperator;
  /** Regex operator - will be evaluated to true if the given value matches the provided regex */
  like?: LikeOperator;
  /** Petri experiment - will be evaluated using petri. */
  experiment?: ExperimentOperator;
  /** Operator that indicates a dependency on another subject being allowed to perform something. */
  dependOn?: DependOnOperator;
}

/** @oneof */
export interface ConditionOperatorOperatorsOneOf {
  /** Comparison of equality - will be evaluated to true if the given parties are equal */
  equals?: EqualOperator;
  /** Regex operator - will be evaluated to true if the given value matches the provided regex */
  like?: LikeOperator;
  /** Petri experiment - will be evaluated using petri. */
  experiment?: ExperimentOperator;
  /** Operator that indicates a dependency on another subject being allowed to perform something. */
  dependOn?: DependOnOperator;
}

export interface EqualOperator {
  /** The attribute which should be compared. The attribute will be first evaluated to a value, and then compared the other side (attribute/value) */
  attrName?: string;
  /** The value to compare to. If the two parties are equal - we will return true. */
  value?: ConditionValue;
}

export interface ConditionValue extends ConditionValueValueOneOf {
  /** an attribute. We'll first retrieve the value of the attribute (from the request or from pre-indexed values), and then compare to what it needs to be compared with. */
  attrName?: string;
  /** a value with a string type. Will be compared to the attribute provided, and be true only if they match the operator. */
  stringValue?: string;
  /** a value with a boolean type. Will be compared to the attribute provided, and be true only if they match the operator. */
  boolValue?: boolean;
}

/** @oneof */
export interface ConditionValueValueOneOf {
  /** an attribute. We'll first retrieve the value of the attribute (from the request or from pre-indexed values), and then compare to what it needs to be compared with. */
  attrName?: string;
  /** a value with a string type. Will be compared to the attribute provided, and be true only if they match the operator. */
  stringValue?: string;
  /** a value with a boolean type. Will be compared to the attribute provided, and be true only if they match the operator. */
  boolValue?: boolean;
}

export interface LikeOperator {
  /** The attribute which should be compared. The attribute will be first evaluated to a value, and then compared the regex values provided. */
  attrName?: string;
  /** The regex values which the attribute value should be evaluated on. If the attribute value matches at least one of the regular expressions provided - we will return true */
  values?: string[];
}

export interface ExperimentOperator {
  /** The spec to conduct the experiment on. */
  spec?: string;
  /** The value to use if the experiment could not be conducted */
  fallbackValue?: string;
  /** The expected value of the experiment conduction. If it matches the actual value - true will be returned. Otherwise - false. */
  expectedValue?: string;
}

/** Implies that the policy takes affect only if the depend on subject is permitted as well. */
export interface DependOnOperator {
  /** The subject on which the current entry depends on. If the subject is allowed to perform what the query was about - the condition will be evaluated to true. Otherwise - false */
  dependOnSubject?: Subject;
}

export interface PolicyUnassigned {
  subject?: Subject;
  assignment?: PolicyAssignment[];
}

export interface PolicyAssignmentUpdated {
  subject?: Subject;
  fromAssignment?: PolicyAssignment[];
  toAssignment?: PolicyAssignment[];
}

export interface Empty {}

export interface PolicyAuthorizationDataUpdated {
  policyId?: string;
  fromPolicyStatements?: PolicyStatement[];
  toPolicyStatements?: PolicyStatement[];
  policyType?: PolicyTypeWithLiterals;
  policyOwner?: PolicyOwner;
}

export interface PolicyStatement {
  /** @readonly */
  _id?: string | null;
  permissions?: string[];
  scopes?: string[];
  effect?: EffectWithLiterals;
  condition?: PolicyCondition;
}

export enum Effect {
  UNKNOWN_EFFECT = 'UNKNOWN_EFFECT',
  ALLOW = 'ALLOW',
  DENY = 'DENY',
}

/** @enumType */
export type EffectWithLiterals = Effect | 'UNKNOWN_EFFECT' | 'ALLOW' | 'DENY';

export enum PolicyType {
  UNKNOWN_STATUS = 'UNKNOWN_STATUS',
  PREDEFINED = 'PREDEFINED',
  CUSTOM = 'CUSTOM',
  INLINE_CUSTOM = 'INLINE_CUSTOM',
}

/** @enumType */
export type PolicyTypeWithLiterals =
  | PolicyType
  | 'UNKNOWN_STATUS'
  | 'PREDEFINED'
  | 'CUSTOM'
  | 'INLINE_CUSTOM';

export interface PolicyOwner {
  type?: PolicyOwnerTypeWithLiterals;
  _id?: string;
}

export enum PolicyOwnerType {
  UNKNOWN_TYPE = 'UNKNOWN_TYPE',
  WIX = 'WIX',
  WIX_ACCOUNT = 'WIX_ACCOUNT',
}

/** @enumType */
export type PolicyOwnerTypeWithLiterals =
  | PolicyOwnerType
  | 'UNKNOWN_TYPE'
  | 'WIX'
  | 'WIX_ACCOUNT';

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

/** @docsIgnore */
export type GetPermissionsApplicationErrors = {
  code?: 'WDE0025';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdatePermissionsApplicationErrors = {
  code?: 'WDE0025';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type AddSpecialPermissionsApplicationErrors = {
  code?: 'ALREADY_EXISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateSpecialPermissionsApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type RemoveSpecialPermissionsApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type GetMyPermissionsApplicationErrors = {
  code?: 'WDE0025';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Retrieves the current permissions for the specified data collection.
 * @param dataCollectionId - ID of the collection to retrieve permissions of.
 * @public
 * @requiredField dataCollectionId
 * @permissionId WIX_DATA.PERMISSIONS_READ
 * @applicableIdentity APP
 * @fqn wix.cloud.permissions.v1.DataPermissionsService.GetPermissions
 */
export async function getPermissions(dataCollectionId: string): Promise<
  NonNullablePaths<
    GetPermissionsResponse,
    | `dataPermissions._id`
    | `dataPermissions.itemRead`
    | `dataPermissions.itemInsert`
    | `dataPermissions.itemUpdate`
    | `dataPermissions.itemRemove`
    | `dataPermissions.specialPermissions`
    | `dataPermissions.specialPermissions.${number}.userId`
    | `dataPermissions.specialPermissions.${number}.policyId`
    | `dataPermissions.specialPermissions.${number}._id`
    | `dataPermissions.specialPermissions.${number}.itemRead`
    | `dataPermissions.specialPermissions.${number}.itemInsert`
    | `dataPermissions.specialPermissions.${number}.itemUpdate`
    | `dataPermissions.specialPermissions.${number}.itemRemove`,
    5
  > & {
    __applicationErrorsType?: GetPermissionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts = ambassadorWixDataV1DataPermissions.getPermissions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates permissions for the specified data collection.
 *
 * > **Note:** To update special permissions, call [Update Special Permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/update-special-permissions).
 * @param dataPermissions - Permissions to update.
 * @public
 * @requiredField dataPermissions
 * @requiredField dataPermissions._id
 * @requiredField dataPermissions.itemInsert
 * @requiredField dataPermissions.itemRead
 * @requiredField dataPermissions.itemRemove
 * @requiredField dataPermissions.itemUpdate
 * @permissionId WIX_DATA.PERMISSIONS_UPDATE
 * @applicableIdentity APP
 * @fqn wix.cloud.permissions.v1.DataPermissionsService.UpdatePermissions
 */
export async function updatePermissions(
  dataPermissions: NonNullablePaths<
    DataPermissions,
    `_id` | `itemInsert` | `itemRead` | `itemRemove` | `itemUpdate`,
    2
  >
): Promise<
  NonNullablePaths<
    UpdatePermissionsResponse,
    | `dataPermissions._id`
    | `dataPermissions.itemRead`
    | `dataPermissions.itemInsert`
    | `dataPermissions.itemUpdate`
    | `dataPermissions.itemRemove`
    | `dataPermissions.specialPermissions`
    | `dataPermissions.specialPermissions.${number}.userId`
    | `dataPermissions.specialPermissions.${number}.policyId`
    | `dataPermissions.specialPermissions.${number}._id`
    | `dataPermissions.specialPermissions.${number}.itemRead`
    | `dataPermissions.specialPermissions.${number}.itemInsert`
    | `dataPermissions.specialPermissions.${number}.itemUpdate`
    | `dataPermissions.specialPermissions.${number}.itemRemove`,
    5
  > & {
    __applicationErrorsType?: UpdatePermissionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataPermissions: dataPermissions,
  });

  const reqOpts = ambassadorWixDataV1DataPermissions.updatePermissions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataPermissions: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataPermissions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds special permissions to a specific user or role for the specified data collection.
 *
 * Special permissions allow specific users or roles to perform actions that [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions) restrict.
 * @param dataCollectionId - ID of the collection for which to grant special permissions.
 * @param specialPermissions - Special permissions to grant to the user or role for the specified collection.
 * @public
 * @requiredField dataCollectionId
 * @requiredField specialPermissions
 * @permissionId WIX_DATA.PERMISSIONS_UPDATE_SPECIAL
 * @applicableIdentity APP
 * @fqn wix.cloud.permissions.v1.DataPermissionsService.AddSpecialPermissions
 */
export async function addSpecialPermissions(
  dataCollectionId: string,
  specialPermissions: SpecialPermissions
): Promise<
  NonNullablePaths<
    AddSpecialPermissionsResponse,
    | `specialPermissions.userId`
    | `specialPermissions.policyId`
    | `specialPermissions._id`
    | `specialPermissions.itemRead`
    | `specialPermissions.itemInsert`
    | `specialPermissions.itemUpdate`
    | `specialPermissions.itemRemove`,
    3
  > & {
    __applicationErrorsType?: AddSpecialPermissionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    specialPermissions: specialPermissions,
  });

  const reqOpts =
    ambassadorWixDataV1DataPermissions.addSpecialPermissions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          specialPermissions: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'specialPermissions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates the special permissions for the specified user or role.
 *
 * > **Note:** Special permissions that aren't explicitly included in the request are set to `UNSPECIFIED` and replaced by [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions).
 * @param _id - ID of the special permissions set.
 * @public
 * @requiredField _id
 * @requiredField specialPermissions
 * @permissionId WIX_DATA.PERMISSIONS_UPDATE_SPECIAL
 * @applicableIdentity APP
 * @fqn wix.cloud.permissions.v1.DataPermissionsService.UpdateSpecialPermissions
 */
export async function updateSpecialPermissions(
  _id: string,
  specialPermissions: UpdateSpecialPermissions
): Promise<
  NonNullablePaths<
    UpdateSpecialPermissionsResponse,
    | `specialPermissions.userId`
    | `specialPermissions.policyId`
    | `specialPermissions._id`
    | `specialPermissions.itemRead`
    | `specialPermissions.itemInsert`
    | `specialPermissions.itemUpdate`
    | `specialPermissions.itemRemove`,
    3
  > & {
    __applicationErrorsType?: UpdateSpecialPermissionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    specialPermissions: { ...specialPermissions, id: _id },
  });

  const reqOpts =
    ambassadorWixDataV1DataPermissions.updateSpecialPermissions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { specialPermissions: '$[1]' },
        explicitPathsToArguments: { 'specialPermissions.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'specialPermissions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateSpecialPermissions {
  /**
   * ID of the user for these special permissions.
   * @immutable
   * @maxLength 64
   */
  userId?: string;
  /**
   * ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.
   *
   * To find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.
   *
   * Learn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).
   * @immutable
   * @maxLength 64
   */
  policyId?: string;
  /**
   * ID of the special permissions set.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Whether the specified users or roles can read items from the collection.
   *
   * When set to `ALLOWED`, users can read items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemRead?: AccessWithLiterals;
  /**
   * Whether the specified users or roles can create new items in the collection.
   *
   * When set to `ALLOWED`, users can create new items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemInsert?: AccessWithLiterals;
  /**
   * Whether the specified users or roles can update existing items in the collection.
   *
   * When set to `ALLOWED`, users can update existing items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemUpdate?: AccessWithLiterals;
  /**
   * Whether the specified users or roles can remove items from the collection.
   *
   * When set to `ALLOWED`, users can remove items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.
   *
   * Learn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).
   */
  itemRemove?: AccessWithLiterals;
  /**
   * Whether the specified users or roles can manage drafts and publish/unpublish data items in the collection.
   * When set to `ALLOWED`, users can manage drafts and publish/unpublish data items.
   * @internal
   */
  draftItemsPluginPermissions?: SpecialPermissionsDraftItemsPluginPermissions;
}

/**
 * Removes special permissions from a specific user or role.
 *
 * When you remove special permissions, [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply to the user or role for all actions.
 * @param specialPermissionsId - ID of the special permissions to remove.
 * @public
 * @requiredField specialPermissionsId
 * @permissionId WIX_DATA.PERMISSIONS_UPDATE_SPECIAL
 * @applicableIdentity APP
 * @fqn wix.cloud.permissions.v1.DataPermissionsService.RemoveSpecialPermissions
 */
export async function removeSpecialPermissions(
  specialPermissionsId: string
): Promise<
  void & {
    __applicationErrorsType?: RemoveSpecialPermissionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    specialPermissionsId: specialPermissionsId,
  });

  const reqOpts =
    ambassadorWixDataV1DataPermissions.removeSpecialPermissions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { specialPermissionsId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['specialPermissionsId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves the current user's permissions for the specified data collection.
 * @param dataCollectionId - ID of the collection for which to check the current user's permissions.
 * @public
 * @requiredField dataCollectionId
 * @permissionId WIX_DATA.PERMISSIONS_GET_ALLOWED
 * @applicableIdentity APP
 * @fqn wix.cloud.permissions.v1.DataPermissionsService.GetMyPermissions
 */
export async function getMyPermissions(dataCollectionId: string): Promise<
  NonNullablePaths<
    GetMyPermissionsResponse,
    `itemRead` | `itemInsert` | `itemUpdate` | `itemRemove`,
    2
  > & {
    __applicationErrorsType?: GetMyPermissionsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts = ambassadorWixDataV1DataPermissions.getMyPermissions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
